import React, { useState, forwardRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Input.css';

/**
 * Input Component
 * A text input with label, error states, icons, and validation
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Input label text
 * @param {string} props.type - Input type (text, email, password, etc.)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field indicator
 * @param {React.ReactNode} props.leftIcon - Icon on the left
 * @param {React.ReactNode} props.rightIcon - Icon on the right
 * @param {string} props.className - Additional CSS classes
 */
const Input = forwardRef(
  (
    {
      label,
      type = 'text',
      placeholder = '',
      value,
      onChange,
      error,
      disabled = false,
      required = false,
      leftIcon,
      rightIcon,
      // Aliases: callers in Login/Register use `icon`/`iconRight`. Strip these
      // here so they don't leak onto the underlying DOM input via `...props`
      // (which causes "React does not recognize the `iconRight` prop" warning).
      icon,
      iconRight,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const resolvedLeftIcon = leftIcon || icon;
    const resolvedRightIcon = rightIcon || iconRight;

    const containerClasses = `
      input-container
      ${error ? 'input-container--error' : ''}
      ${disabled ? 'input-container--disabled' : ''}
      ${isFocused ? 'input-container--focused' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const inputClasses = `
      input-field
      ${resolvedLeftIcon ? 'input-field--with-left-icon' : ''}
      ${resolvedRightIcon ? 'input-field--with-right-icon' : ''}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className="input-label">
            {label}
            {required && <span className="input-required">*</span>}
          </label>
        )}

        <div className="input-wrapper">
          {resolvedLeftIcon && (
            <span className="input-icon input-icon--left">{resolvedLeftIcon}</span>
          )}

          <motion.input
            ref={ref}
            id={inputId}
            name={name}
            type={type}
            className={inputClasses}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            required={required}
            aria-label={label || placeholder}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            whileFocus={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
            {...props}
          />

          {resolvedRightIcon && (
            <span className="input-icon input-icon--right">{resolvedRightIcon}</span>
          )}
        </div>

        {error && (
          <motion.span
            id={`${inputId}-error`}
            className="input-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            role="alert"
          >
            {error}
          </motion.span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  icon: PropTypes.node,
  iconRight: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Input;
